import { z } from 'zod';

export const zoomSeatUsageSchema = z.object({
  hosts: z.number(),
  usage: z.number(),
  pending: z.number(),
  available: z.number(),
});

export type ZoomSeatUsage = z.infer<typeof zoomSeatUsageSchema>;

/** Roles that receive a Zoom Licensed seat (supervisor stack includes provider). */
export function rolesNeedZoomSeat(roles: readonly string[]): boolean {
  return roles.some((role) => role === 'provider' || role === 'supervisor');
}

/** Block create / add-role while seats are loading or exhausted. */
export function zoomSeatsBlockSubmit(usage: ZoomSeatUsage | null, loading: boolean): boolean {
  if (loading) return true;
  return usage != null && usage.available <= 0;
}

/** Short status line for System Integrations Zoom license card. */
export function zoomSeatUsageSummary(usage: ZoomSeatUsage): string {
  const pending =
    usage.pending > 0
      ? `, ${usage.pending} pending invite${usage.pending === 1 ? '' : 's'}`
      : '';
  return `${usage.available} of ${usage.hosts} licenses available (${usage.usage} assigned${pending})`;
}

export type ZoomProvisionOutcome = 'created' | 'linked' | 'upgraded';

/**
 * Confirmation copy after an invite. Only `created` sends a Zoom activation email —
 * `linked` reuses an existing Licensed Zoom user and consumes no new seat.
 */
export function zoomProvisionMessage(name: string, outcome?: ZoomProvisionOutcome): string {
  switch (outcome) {
    case 'created':
      return `${name} has been added. They will receive a Unify setup email and a Zoom activation email — they must confirm the Zoom email before hosting telehealth sessions.`;
    case 'linked':
      return `${name} has been added and linked to their existing Zoom account. No Zoom activation email was sent and no additional Zoom license was used.`;
    case 'upgraded':
      return `${name} has been added and their existing Zoom account was upgraded to a Licensed seat. No Zoom activation email was sent.`;
    default:
      return `${name} has been added to the practice.`;
  }
}
